import { env } from '@/env/client'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Generate a random hex color
 * @returns A random hex color
 */
export function randomHexColor() {
  return `#${Math.floor(Math.random() * 16777215).toString(16)}`
}

/**
 * Format bytes to a human readable format (e.g. 1024 -> 1 KB, 1024000 -> 1 MB)
 * @param bytes - The number of bytes to format
 * @param opts - Options for the formatting
 * @param opts.decimals - The number of decimals to include in the formatted string
 * @param opts.sizeType - The type of size to use (accurate or normal)
 * @returns The formatted string
 */
export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number
    sizeType?: 'accurate' | 'normal'
  } = {}
) {
  const { decimals = 0, sizeType = 'normal' } = opts

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const accurateSizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB']
  if (bytes === 0) return '0 Byte'
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / 1024 ** i).toFixed(decimals)} ${
    sizeType === 'accurate'
      ? (accurateSizes[i] ?? 'Bytest')
      : (sizes[i] ?? 'Bytes')
  }`
}

export function absoluteUrl(path: string) {
  if (path.startsWith(env.NEXT_PUBLIC_URL)) {
    return path
  }
  if (path.startsWith('/')) {
    return `${env.NEXT_PUBLIC_URL}${path}`
  }
  return `${env.NEXT_PUBLIC_URL}/${path}`
}

/**
 * Stole this from the @radix-ui/primitive
 * @see https://github.com/radix-ui/primitives/blob/main/packages/core/primitive/src/primitive.tsx
 */
export function composeEventHandlers<E>(
  originalEventHandler?: (event: E) => void,
  ourEventHandler?: (event: E) => void,
  { checkForDefaultPrevented = true } = {}
) {
  return function handleEvent(event: E) {
    originalEventHandler?.(event)

    if (
      checkForDefaultPrevented === false ||
      !(event as unknown as Event).defaultPrevented
    ) {
      return ourEventHandler?.(event)
    }
  }
}

/**
 * Just sleep for `ms` milliseconds
 * @param ms : The number of milliseconds
 * @returns Promise<void>
 */
export async function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function capitalize(str: string) {
  const s = str.split(/[-_\s]/)
  return s.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

export function pluralize(count: number, word: string, plural = `${word}s`) {
  return count > 1 ? plural : word
}
